import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { UserType, type UserTypeValue } from '../../constants/userType';
import type { TSYSTEM_PERMISSIONS } from '../../constants/permissions';

export type ThemeMode = 'light' | 'dark' | 'ocean';

export interface PartnerInState {
  id: string;
  name: string;
  subdomain?: string;
  platformFee?: number;
  industry?: {
    id: string;
    name: string;
    type: string;
  };
  externalId?: string;
  subscriptionPlanId?: string;
  exchangeRateCode: 'USD' | 'EUR';
}

export interface AuthState {
  token: string | null;
  partner: PartnerInState | null;
  allowedStores: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    mapsUrl?: string;
  }[];
  activeStoreId: string | null;
  theme: ThemeMode;
  userType: UserTypeValue | null;
  isSuperadmin: boolean;
  role: string | null;
  permissions: string[];
  allowedPermissions: TSYSTEM_PERMISSIONS[];
  adminSession?: {
    token: string;
    partner: PartnerInState | null;
    allowedStores: {
      id: string;
      name: string;
      latitude: number;
      longitude: number;
      mapsUrl?: string;
    }[];
    activeStoreId: string | null;
    userType: UserTypeValue;
    isSuperadmin: boolean;
    allowedPermissions: TSYSTEM_PERMISSIONS[];
  } | null;
}

const initialState: AuthState = {
  token: null,
  partner: null,
  allowedStores: [],
  activeStoreId: null,
  theme: 'light',
  userType: null,
  isSuperadmin: false,
  role: null,
  permissions: [],
  allowedPermissions: [],
  adminSession: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        token: string;
        partner?: {
          id: string;
          name: string;
          industry?: { id: string; name: string; type: string };
          exchangeRateCode: 'USD' | 'EUR';
        } | null;
        allowedStores?: AuthState['allowedStores'] | null;
        allowedPermissions?: TSYSTEM_PERMISSIONS[];
        type: UserTypeValue;
        isSuperadmin?: boolean;
      }>
    ) => {
      const { token, partner, allowedStores, allowedPermissions, type, isSuperadmin } =
        action.payload;
      state.token = token;
      state.partner = partner || null;
      state.allowedStores = allowedStores || [];
      state.allowedPermissions = allowedPermissions || [];
      state.userType = type;
      state.isSuperadmin = isSuperadmin || false;

      if (allowedStores && allowedStores.length === 1) {
        state.activeStoreId = allowedStores[0].id;
      } else {
        state.activeStoreId = null;
        state.role = null;
        state.permissions = [];
      }
    },
    setActiveStore: (state, action: PayloadAction<string>) => {
      state.activeStoreId = action.payload;
    },
    setRolePermissions: (state, action: PayloadAction<{ role: string; permissions: string[] }>) => {
      state.role = action.payload.role;
      state.permissions = action.payload.permissions;
    },
    toggleTheme: (state) => {
      // Binario clásico (otros paquetes lo usan así): light ↔ dark.
      // El tercer tema ('ocean') solo lo expone sama-partner vía setTheme.
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
    },
    updatePartnerCurrency: (state, action: PayloadAction<'USD' | 'EUR'>) => {
      if (state.partner) {
        state.partner.exchangeRateCode = action.payload;
      }
    },
    logout: (state) => {
      state.token = null;
      state.partner = null;
      state.allowedStores = [];
      state.activeStoreId = null;
      state.userType = null;
      state.isSuperadmin = false;
      state.role = null;
      state.permissions = [];
      state.allowedPermissions = [];
      state.adminSession = null;
    },
    impersonateTenant: (
      state,
      action: PayloadAction<{
        partner: AuthState['partner'];
        allowedStores: AuthState['allowedStores'];
        allowedPermissions: TSYSTEM_PERMISSIONS[];
      }>
    ) => {
      state.adminSession = {
        token: state.token!,
        partner: state.partner,
        allowedStores: state.allowedStores,
        activeStoreId: state.activeStoreId,
        userType: state.userType!,
        isSuperadmin: state.isSuperadmin,
        allowedPermissions: state.allowedPermissions,
      };

      state.partner = action.payload.partner;
      state.allowedStores = action.payload.allowedStores;
      state.allowedPermissions = action.payload.allowedPermissions || [];
      state.userType = UserType.tenant;
      state.isSuperadmin = true;

      if (action.payload.allowedStores && action.payload.allowedStores.length > 0) {
        state.activeStoreId = action.payload.allowedStores[0].id;
      } else {
        state.activeStoreId = null;
      }
    },
    stopImpersonating: (state) => {
      const session = state.adminSession;

      if (session) {
        state.token = session.token;
        state.partner = session.partner;
        state.allowedStores = session.allowedStores;
        state.activeStoreId = session.activeStoreId;
        state.userType = session.userType;
        state.isSuperadmin = session.isSuperadmin;
        state.allowedPermissions = session.allowedPermissions;

        state.adminSession = null;
      }
    },
  },
});

export const {
  setCredentials,
  setActiveStore,
  setRolePermissions,
  logout,
  toggleTheme,
  setTheme,
  impersonateTenant,
  stopImpersonating,
  updatePartnerCurrency,
} = authSlice.actions;

export const selectUserType = (state: { auth: AuthState }) => state.auth.userType;
export const selectCurrencyCode = (state: { auth: AuthState }) =>
  state.auth.partner?.exchangeRateCode;

export default authSlice.reducer;
